import { useState, useEffect } from "react";
import { formatDistanceToNow } from "date-fns";
import { ShieldCheck, Smartphone, Laptop, Loader2, X, Clock, MapPin } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { authClient } from "@/lib/auth-client";
import { toast } from "@/components/ui/sonner";

interface Session {
  id: string;
  ipAddress?: string;
  userAgent?: string;
  createdAt: Date;
  expiresAt: Date;
  isCurrent: boolean;
}

export const TrustedDevicesManager = () => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState<string | null>(null);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const { data, error } = await authClient.listSessions();
      if (error) throw error;
      
      setSessions(data.map((s: any) => ({
        id: s.id,
        ipAddress: s.ip_address, // Corrected from s.ipAddress to s.ip_address
        userAgent: s.user_agent, // Corrected from s.userAgent to s.user_agent
        createdAt: new Date(s.created_at), // Corrected from s.createdAt to s.created_at
        expiresAt: new Date(s.expires_at), // Corrected from s.expiresAt to s.expires_at
        isCurrent: s.is_current, // Corrected from s.isCurrent to s.is_current
      })) || []);
    } catch (error) {
      console.error('Error loading sessions:', error);
      toast.error("Failed to load active sessions");
    } finally {
      setLoading(false);
    }
  };

  const revokeSession = async (sessionId: string) => {
    setRevoking(sessionId);
    try {
      // Note: Better Auth's revokeSession typically expects a token. 
      // If listSessions doesn't provide the token for other sessions, 
      // we might need to rely on the ID if the client supports it, 
      // or this might only work for the current session if token is required.
      // For now, we pass the ID as the token property, assuming the API might handle it 
      // or we need to adjust based on specific Better Auth version behavior.
      const { error } = await authClient.revokeSession({
        id: sessionId // Corrected from token: sessionId to id: sessionId
      });

      if (error) throw error;

      toast.success("Session revoked");
      setSessions(sessions.filter(s => s.id !== sessionId));
    } catch (error: any) {
      console.error('Error revoking session:', error);
      toast.error("Failed to revoke session");
    } finally {
      setRevoking(null);
    }
  };

  const getDeviceIcon = (userAgent?: string) => {
    if (!userAgent) return <Smartphone className="h-5 w-5 text-muted-foreground" />;
    if (userAgent.toLowerCase().includes("mobile")) return <Smartphone className="h-5 w-5 text-muted-foreground" />;
    return <Laptop className="h-5 w-5 text-muted-foreground" />;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" />
              Active Sessions
            </CardTitle>
            <CardDescription>
              Manage devices where you are currently logged in
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {sessions.length === 0 && (
          <p className="text-muted-foreground text-center py-4">No active sessions found.</p>
        )}

        {sessions.map((session) => (
          <div 
            key={session.id} 
            className="flex items-start justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
          >
            <div className="flex items-start gap-4 flex-1 min-w-0">
              <div className="mt-1">
                {session.isCurrent ? (
                  <ShieldCheck className="h-5 w-5 text-green-500 shrink-0" />
                ) : (
                  getDeviceIcon(session.userAgent)
                )}
              </div>
              <div className="flex-1 min-w-0 space-y-2">
                <div className="font-medium flex items-center gap-2 flex-wrap">
                  <span className="truncate">{session.userAgent || "Unknown Device"}</span>
                  {session.isCurrent && (
                    <Badge variant="outline" className="bg-green-500/10 text-green-700 dark:text-green-400">
                      Current Session
                    </Badge>
                  )}
                </div>
                
                <div className="space-y-1 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Clock className="h-3.5 w-3.5 shrink-0" />
                    <span>Started {formatDistanceToNow(session.createdAt)} ago</span>
                  </div>
                  
                  {session.ipAddress && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-3.5 w-3.5 shrink-0" />
                      <span>IP: {session.ipAddress}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {!session.isCurrent && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => revokeSession(session.id)}
                disabled={revoking === session.id}
                className="ml-4 shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                {revoking === session.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <X className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
